import React, { useState, useRef, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import theme from "../../theme";

import Box from "@commerce-ui/core2/Box";
import MenuLayout from "@commerce-ui/core2/MenuLayout";
import Button from "@commerce-ui/core2/Button";
import Container from "@commerce-ui/core2/Container";
import LinkRaw from "@commerce-ui/core2/LinkRaw";
import useScrollDirection from "@commerce-ui/core2/useScrollDirection";
import useScrollSegment from "@commerce-ui/core2/useScrollSegment";
import useWindowSize from "@commerce-ui/core2/useWindowSize";

import Header from "../../sections/Header";
import { headerLinks } from "@deosite/mock-data/headerLinks";

import Image from "@deosite/components-new/Image";

import { useRouteContext } from "@deosite/lib/RouteContext";
import useDebounce from "@deosite/lib/useDebounce";
import {
  disableBodyScroll,
  clearAllBodyScrollLocks
} from "@deosite/lib/bodyScrollLock";

import { useRouter } from "next/router";
import SearchDesktop from "../../components/SearchDesktop";

import { useLoader } from "@deosite/api/hooks/common/useLoader";
import ArrowRightIcon from "../../icons/arrow_right.svg";
import Modal from "react-modal";
import SearchMobile from "../../components/SearchMobile";
const MenuMobileContent = dynamic(() =>
  import("@deosite/components-new/MenuMobileContent")
);
import {
  TIMEOUT,
  externalContentTimeout
} from "@deosite/helpers/prismicErrorHandle";

const MenuButton = ({ children, isSpecial, ...restProps }) => (
  <Button {...restProps}>
    {({ isHovered, selected }) => (
      <Box
        sx={{
          font: "xs_caps",
          color: isSpecial === "sale" ? "negative" : "main",
          px: [0, null, null, null, 7, 10],
          pb: 14,
          position: "relative",
          textAlign: "center"
        }}
      >
        <Box sx={{ position: "relative" }}>
          {children}
          <Box
            sx={{
              opacity: isHovered || selected ? 1 : 0,
              transition: "opacity .15s",
              position: "absolute",
              width: "100%",
              height: "2px",
              bg: isSpecial === "sale" ? "negative" : "main"
            }}
          />
        </Box>
      </Box>
    )}
  </Button>
);

const addSlash = href => (href.indexOf("/") === 0 ? href : "/" + href);

const Submenu = React.memo(({ submenu }) => {
  const { Link } = useRouteContext();

  const isSale = submenu.special && submenu.special === "sale";

  const props = submenu.hasSpecialUrl
    ? { href: addSlash(submenu.url) }
    : { route: "category-page", params: submenu };

  const columnWidth = ["150px", null, null, null, null, "180px"];
  return (
    <Box as={"ul"} key={"fragment-" + submenu.name} sx={{ width: columnWidth }}>
      <Box
        as={"li"}
        sx={{
          font: "base_sans_bold",
          color: isSale ? "negative" : "main",
          mt: "s8",
          width: columnWidth
        }}
      >
        <Link {...props}>
          <LinkRaw title={submenu.name}>{submenu.name}</LinkRaw>
        </Link>
      </Box>
      <Box>
        {submenu.subcategories &&
          Object.keys(submenu.subcategories).map((item, index) => {
            if (!submenu.subcategories[item]) {
              return;
            }

            const props = submenu.subcategories[item].hasSpecialUrl
              ? { href: submenu.subcategories[item].url }
              : { route: "category-page", params: submenu.subcategories[item] };
            return (
              <Box
                as={"ul"}
                key={"submenuLinks-" + index}
                sx={{ width: columnWidth }}
              >
                <Box
                  as="li"
                  sx={{
                    font: "s_sans",
                    color: "main",
                    mt: index === 0 ? "s5" : "s3",
                    width: columnWidth,
                    "&:hover": { textDecoration: "underline" }
                  }}
                  key={submenu.subcategories[item].url}
                >
                  <Link {...props}>
                    <LinkRaw title={submenu.subcategories[item].name}>
                      {submenu.subcategories[item].name}
                    </LinkRaw>
                  </Link>
                </Box>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
});

const MainMenu = React.memo(
  ({
    menuContent,
    onlyHeader,
    isScrollingDown,
    menuBanners,
    isMobile,
    popularSearches,
    hasSubmenu
  }) => {
    const { Link } = useRouteContext();
    const { isLoading } = useLoader();
    const [isOpen, setOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const router = useRouter();

    const minifiedContent = router.asPath.slice(0, 9) == "/checkout";

    useEffect(() => {
      const element = document?.getElementById("layerUi");
      if (mobileSearchOpen) {
        disableBodyScroll(element);
      } else {
        clearAllBodyScrollLocks();
      }
    }, [mobileSearchOpen]);

    const mobileMenu = () => {
      return (
        <>
          {" "}
          {isMobile === true && (
            <Modal
              isOpen={isOpen}
              style={{
                overlay: {
                  backgroundColor: "rgba(0, 0, 0, 0)"
                },
                content: {
                  inset: 0,
                  padding: 0,
                  border: "none",
                  backgroundColor: "rgba(0, 0, 0, 0.3)"
                }
              }}
              preventScroll={true}
            >
              <Box
                sx={{
                  width: ["100vw", null, "50vw", null, "33vw"],
                  bg: "white",
                  minHeight: "100vh"
                }}
              >
                <MenuMobileContent
                  categoryList={menuContent}
                  pageLinks={[]}
                  closeMenu={() => setOpen(false)}
                  router={router}
                ></MenuMobileContent>
              </Box>
            </Modal>
          )}
          {isMobile === true && (
            <Modal
              isOpen={mobileSearchOpen}
              style={{
                overlay: {
                  background: "white",
                  height: "100vh"
                },
                content: {
                  width: "100vw",
                  inset: 0,
                  padding: 0
                }
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  bg: "white",
                  p: 2
                }}
              >
                <SearchMobile
                  popularSearches={popularSearches}
                  closeSearch={setMobileSearchOpen}
                />
              </Box>
            </Modal>
          )}
        </>
      );
    };

    return (
      <>
        <Box>
          <Header
            headerLinks={headerLinks}
            onMobileMenuClick={() => setOpen(true)}
            minifiedContent={minifiedContent}
            checkoutUrl={minifiedContent && router.asPath}
            helpLink={"/info/help"}
            desktopSearch={isMobile === false && <SearchDesktop />}
            scrollingPart={!onlyHeader}
            onSearchClick={() => setMobileSearchOpen(true)}
          />
        </Box>
        <Container
          sx={{
            display: onlyHeader ? "none" : ["none", null, null, null, "block"],
            position: "static"
          }}
        >
          {mobileMenu()}
          {!minifiedContent && (
            <Box
              data-test={"main-categories"}
              sx={{
                color: "main",
                font: "xs_caps",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                position: isScrollingDown || isMobile ? "absolute" : "static",
                left: 0,
                top: "-64px",
                pt: "s8",
                pl: isScrollingDown ? "130px" : 0,
                pr: isScrollingDown ? "65px" : 0
              }}
            >
              {menuContent?.map((mainMenu, index) => {
                const props = mainMenu.hasSpecialUrl
                  ? { href: addSlash(mainMenu.url) }
                  : { route: "category-page", params: mainMenu };

                const defaultColor = { color: "inherit" };
                const specialCategoryCss = { color: "#FFFFFF", backgroundColor: "#B22222", paddingTop: 2, paddingX: 4};
                const blackCategoryCss = {
                  color: "white",
                  backgroundColor: "black",
                  paddingTop: 2,
                  paddingX: 4
                };

                const boxingCategoryCss = {
                  color: "white",
                  backgroundColor: "blueDark",
                  paddingTop: 2,
                  paddingX: 4
                };

                const harvestFestivalCategoryCss = {
                  color: "#000000",
                  backgroundColor: "#FFBA00",
                  paddingTop: 2,
                  paddingX: 4
                };

                let sx = defaultColor;
                if (
                  parseInt(mainMenu.id) ===
                  global.__appConfig__.specialCategoryId
                ) {
                  sx = specialCategoryCss;
                }
                if (
                  parseInt(mainMenu.id) === global.__appConfig__.blackCategoryId
                ) {
                  sx = blackCategoryCss;
                }

                if (
                  parseInt(mainMenu.id) ===
                  global.__appConfig__.harvestFestivalCategoryId
                ) {
                  sx = harvestFestivalCategoryCss;
                }

                if (
                  parseInt(mainMenu.id) ===
                  global.__appConfig__.boxingCategoryId
                ) {
                  sx = boxingCategoryCss;
                }

                return (
                  <MenuLayout.Dialog
                    key={"mainCategory-" + mainMenu.name}
                    button={
                      <MenuButton isSpecial={mainMenu.special}>
                        <Link {...props}>
                          <Box
                            sx={{
                              position: "relative",
                              ...sx
                            }}
                          >
                            {mainMenu.name}
                          </Box>
                        </Link>
                      </MenuButton>
                    }
                    anchoredTo={"window"}
                  >
                    {!isMobile && hasSubmenu && (
                      <Box
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          boxShadow: "0px 6px 8px rgba(0, 0, 0, 0.15)",
                          width: "100vw",
                          display:
                            (mainMenu?.subcategories?.length > 0 ||
                              menuBanners?.find(
                                banner => banner.categoryId === mainMenu.id
                              )) &&
                            !isLoading
                              ? "block"
                              : "none"
                        }}
                      >
                        <Box
                          sx={{
                            pt: "s3",
                            pb: "s11",
                            bg: "white"
                          }}
                        >
                          <Container
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            {mainMenu?.subcategories?.map(
                              (submenuColumn, index) => (
                                <Box
                                  key={"submenuGroup-" + index}
                                  sx={{
                                    width: "180px",
                                    ml: index === 0 ? "0" : "38px"
                                  }}
                                >
                                  {submenuColumn.map((sub, index) => (
                                    <Submenu
                                      key={"submenu-" + index}
                                      submenu={sub}
                                    />
                                  ))}
                                </Box>
                              )
                            )}
                            {menuBanners &&
                              menuBanners.map(
                                (submenu, index) =>
                                  submenu.categoryId === mainMenu.id && (
                                    <Link key={index} href={submenu.url}>
                                      <LinkRaw
                                        sx={{
                                          width: 380,
                                          minWidth: 380,
                                          mt: "s8",
                                          ml: index === 0 ? 80 : "s10",
                                          overflow: "hidden"
                                        }}
                                        title={submenu.title}
                                      >
                                        {submenu.image.originalSrc && (
                                          <Image
                                            sx={{
                                              aspectRatio: 0.75
                                            }}
                                            image={submenu.image}
                                            sizes={"420px"}
                                            cloudflareEnabled={false}
                                          />
                                        )}
                                        <Box
                                          sx={{
                                            font: "base_sans_bold",
                                            color: "main",
                                            mt: "s4",
                                            svg: {
                                              position: "relative",
                                              top: 5,
                                              ml: 5
                                            }
                                          }}
                                        >
                                          {submenu.title} <ArrowRightIcon />
                                        </Box>
                                        <Box
                                          sx={{
                                            font: "s_sans",
                                            color: "main",
                                            mt: "s2"
                                          }}
                                        >
                                          {submenu.subtitle}
                                        </Box>
                                      </LinkRaw>
                                    </Link>
                                  )
                              )}
                          </Container>
                        </Box>
                      </Box>
                    )}
                  </MenuLayout.Dialog>
                );
              })}
            </Box>
          )}
        </Container>
      </>
    );
  }
);

const MenuDesktopLayout = ({
  children,
  menu,
  noBorder,
  menuBanners,
  popularSearches
}) => {
  const { categoryTree } = menu;
  const menuContent = useMemo(() => {
    return categoryTree;
  }, [categoryTree?.[0]?.id]);
  popularSearches = useMemo(() => popularSearches, [
    popularSearches.content?.url
  ]);
  menuBanners =
    menuBanners && menuBanners !== TIMEOUT && !externalContentTimeout
      ? menuBanners
      : null;

  const isScrollingDown = useScrollDirection();
  const isNotAtTop = useScrollSegment({
    from: 1
  });

  const firstRender = useRef(true);
  const [hasSubmenu, setSubmenu] = useState(false);
  const size = useWindowSize();

  const [isMobile, setMobile] = useState(null);
  const { setSubmenuLoaded } = useLoader();
  const setSubmenuInterval = useDebounce(
    parseInt(global.__appConfig__.submenuLoadTimeout),
    () => {
      setSubmenu(true);
      setSubmenuLoaded(true);
    }
  );

  useEffect(() => {
    const isMobileSize = size.width <= parseInt(theme().breakpoints.lg) - 1;
    if (isMobile !== isMobileSize) {
      setMobile(isMobileSize);
    }
  }, [size?.width]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      !hasSubmenu && setSubmenuInterval();
    }
  });

  return (
    <MenuLayout offset={[48, null, null, null, 126]}>
      {!isMobile && (
        <MenuLayout.MenuBar
          sx={{ bg: "white" }}
          open={!isScrollingDown}
          onMouseOver={() => !hasSubmenu && setSubmenu(true)}
        >
          <MainMenu
            menuContent={
              hasSubmenu
                ? menuContent
                : menuContent.map(menuTop => ({
                    ...menuTop,
                    subcategories: []
                  }))
            }
            hasSubmenu={hasSubmenu}
            onlyHeader={true}
            menuBanners={menuBanners}
            isMobile={isMobile}
            popularSearches={popularSearches}
          />
        </MenuLayout.MenuBar>
      )}

      <MenuLayout.MenuBar>
        <Box
          sx={{
            bg: "white",
            color: "main",
            display:
              isNotAtTop || !isScrollingDown || isMobile ? "block" : "flex",
            position:
              isNotAtTop || !isScrollingDown || isMobile
                ? "relative"
                : "static",
            justifyContent: "center",
            alignItems: "center"
          }}
          onMouseOver={() => !hasSubmenu && setSubmenu(true)}
        >
          <MainMenu
            menuContent={
              hasSubmenu
                ? menuContent
                : menuContent.map(menuTop => ({
                    ...menuTop,
                    subcategories: []
                  }))
            }
            hasSubmenu={hasSubmenu}
            isScrollingDown={isScrollingDown}
            menuBanners={menuBanners}
            onlyHeader={isMobile}
            isMobile={isMobile}
            popularSearches={popularSearches}
          />
        </Box>
      </MenuLayout.MenuBar>

      <MenuLayout.MenuBar>
        <Box
          sx={{
            height: [0, null, null, 1],
            bg: "neutral",
            transition: "opacity .2s",
            opacity: isNotAtTop ? 1 : 0
          }}
        />
        <Container>
          <Box
            sx={{
              borderBottom: noBorder ? 0 : isNotAtTop ? 0 : "1px",
              borderStyle: "solid",
              borderColor: ["transparent", null, null, null, "main"]
            }}
          ></Box>
        </Container>
      </MenuLayout.MenuBar>
      {children}
    </MenuLayout>
  );
};

export default MenuDesktopLayout;
